import React, { useMemo, useState } from "react";
import { Row, Col, Spinner, Button } from "react-bootstrap";
import styled from "styled-components";
import AddShoppingCart from "@mui/icons-material/AddShoppingCart";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import SuccessButton from "../../../organisms/SuccessButton";
import Calendar from "../../../organisms/Calendar";
import { HeaderSection } from "../HeaderSection";
import { Title } from "../Title";
import ServerErrorModal from "../../../organisms/ServerErrorModal";
import { useMutation } from "@tanstack/react-query";
import {
  buildNuevoAgendamiento,
  buildNuevoItemCarritoGuarderia,
  Model,
} from "../Model";
import CurrencyFormat from "react-currency-format";
import { parseCurrency } from "../../../../utils/numberUtils";
import { agendar } from "../../../../services/agendamientosGuarderiaServicios";
import ExitoCard, { ExitoOption } from "../../../organisms/ExitoCard";
import PaymentIcon from "@mui/icons-material/Payment";
import Pagar from "../Pagar/Pagar";
import CalculoPrecioTransporte from "../CalculoPrecioTransporte/CalculoPrecioTransporte";
import FechasGuarderia from "../FechasGuarderia/FechasGuarderia";
import { useWizard } from "../../../../hooks/wizard";
import { fechasSonIguales } from "../../../../utils/dateUtils";
import { useCarrito } from "../../../../hooks/carrito";
import InformationModal from "../../../organisms/InformationModal";

function Resumen({ model }: { model: Model }) {
  const [newModel, setNewModel] = useState(recalcularTotal(model));
  const { avanza, vuelve, volver, avanzar } = useWizard();
  const {
    preparationOperationsIsLoading: carritoPreparationOperationsIsLoading,
    errorValidacionInclusionCarrito,
    validarInclusion,
    setErrorValidacionInclusionCarrito,
    mutarAgregarAlCarrito,
    agregarAlCarritoIsLoading,
    agregarAlCarritoIsSuccess,
    isError: carritoOperationsIsError,
  } = useCarrito();

  const diasTransporte = useMemo(() => {
    return newModel.fechasTransporte.length;
  }, [newModel]);

  const {
    error: errorAgendar,
    isLoading: agendarIsLoading,
    mutate: mutarAgendar,
    isSuccess: agendarIsSuccess,
  } = useMutation(agendar);

  const error = errorAgendar || carritoOperationsIsError;

  const onValorFinalDiaIndividualChange = (
    val: React.ChangeEvent<HTMLInputElement>
  ) => {
    setNewModel((prevModel) => {
      const modelActualizado = {
        ...prevModel,
        valorFinalDiaIndividual: parseCurrency(val.target.value, "$", ","),
      };
      return recalcularTotal(modelActualizado);
    });
  };

  const onValorFinalPlanChange = (val: React.ChangeEvent<HTMLInputElement>) => {
    setNewModel((prevModel) => {
      const modelActualizado = {
        ...prevModel,
        valorFinalPlan: parseCurrency(val.target.value, "$", ","),
      };
      return recalcularTotal(modelActualizado);
    });
  };

  const onValorFinalIdaChange = (val: React.ChangeEvent<HTMLInputElement>) => {
    setNewModel((prevModel) => {
      const modelActualizado = {
        ...prevModel,
        valorFinalIda: parseCurrency(val.target.value, "$", ","),
      };
      return recalcularTotal(modelActualizado);
    });
  };

  const onValorFinalVueltaChange = (
    val: React.ChangeEvent<HTMLInputElement>
  ) => {
    setNewModel((prevModel) => {
      const modelActualizado = {
        ...prevModel,
        valorFinalVuelta: parseCurrency(val.target.value, "$", ","),
      };
      return recalcularTotal(modelActualizado);
    });
  };

  const onValorFinalIdaVueltaChange = (
    val: React.ChangeEvent<HTMLInputElement>
  ) => {
    setNewModel((prevModel) => {
      const modelActualizado = {
        ...prevModel,
        valorFinalIdaVuelta: parseCurrency(val.target.value, "$", ","),
      };
      return recalcularTotal(modelActualizado);
    });
  };

  const onDescuentoChange = (val: React.ChangeEvent<HTMLInputElement>) => {
    setNewModel((prevModel) => {
      const targetVal: string | undefined = val.target.value;
      const targetValStr: string = targetVal ? targetVal : "$0";
      const modelActualizado = {
        ...prevModel,
        descuento: parseCurrency(targetValStr, "$", ","),
      };
      return recalcularTotal(modelActualizado);
    });
  };

  const onConfirmarClick = () => {
    const agendamiento = buildNuevoAgendamiento(newModel);
    mutarAgendar(agendamiento);
  };

  const onAgregarAlCarritoClick = () => {
    const itemCarrito = buildNuevoItemCarritoGuarderia(newModel);
    const isExito = validarInclusion(itemCarrito);

    if (isExito) {
      mutarAgregarAlCarrito(itemCarrito);
    }
  };

  const onModalValidacionInclusionCarritoClose = () => {
    setErrorValidacionInclusionCarrito(undefined);
  };

  const FechasTransporte = () => {
    if (model.fechasTransporte.length === 0) return <></>;
    if (fechasSonIguales(model.fechasTransporte, model.fechasProgramadas)) {
      return <InfoRow label="Fechas de transporte" value={"Todas"} />;
    }
    return (
      <div className="mb-2">
        <label className="mb-2" style={{ fontWeight: 500 }}>
          Fechas de transporte:
        </label>
        <StyledCalendar
          preSelectedDates={model.fechasTransporte}
          disableAll={true}
        />
      </div>
    );
  };

  if (agendarIsSuccess || agregarAlCarritoIsSuccess) {
    return <ExitoCard title={"¡Creación exitosa!"} options={exitoOptions} />;
  }

  if (avanza) {
    return <Pagar model={newModel} />;
  }

  if (vuelve) {
    if (newModel.contrataTransporte) {
      return <CalculoPrecioTransporte model={newModel} />;
    }
    return <FechasGuarderia model={newModel} />;
  }

  return (
    <>
      {error && <ServerErrorModal />}
      {errorValidacionInclusionCarrito && (
        <InformationModal
          message={errorValidacionInclusionCarrito.mensaje}
          onClose={onModalValidacionInclusionCarritoClose}
        />
      )}
      <Row className="justify-content-center">
        <Col className="col-11">
          <HeaderSection className="pt-5 mb-4">
            <Title>
              Resúmen de agendamiento
              <IconContainer>
                <FactCheckIcon sx={{ fontSize: 28 }} />
              </IconContainer>
            </Title>
          </HeaderSection>
          <section className="mb-5">
            <SectionTitle className="mb-3">Datos básicos</SectionTitle>
            <InfoRow label="Propietario" value={model.nombresPropietario} />
            <InfoRow label="Mascota" value={model.nombreMascota} />
            <div>
              <label className="mb-2" style={{ fontWeight: 500 }}>
                Fechas programadas:
              </label>
              <StyledCalendar
                preSelectedDates={model.fechasProgramadas}
                disableAll={true}
              />
            </div>
          </section>

          {model.fechasTransporte.length > 0 && (
            <section className="mb-5">
              <SectionTitle className="mb-3">Datos de transporte</SectionTitle>
              <InfoRow
                label="Contrata transporte"
                value={!!model.contrataTransporte ? "Si" : "No"}
              />
              <FechasTransporte />

              <InfoRow
                label="Sector de transporte"
                value={model.sectorTransporte?.nombre || ""}
              />
              <InfoRow
                label="Dirección de transporte"
                value={model.direccionTransporte || ""}
              />
              <InfoRow
                label="Ida"
                value={
                  !!model.contrataIda && !!model.contrataTransporte
                    ? "Si"
                    : "No"
                }
              />
              <InfoRow
                label="Vuelta"
                value={
                  !!model.contrataVuelta && !!model.contrataTransporte
                    ? "Si"
                    : "No"
                }
              />
            </section>
          )}

          <section className="mb-4">
            <SectionTitle className="mb-3">Tarifas y planes</SectionTitle>
            {model.plan && (
              <Row className="mb-2">
                <LabelCol className="col-5">
                  {model.plan!!.nombre + ":"}
                </LabelCol>
                <Col></Col>
                <Col className="text-right">
                  <CurrencyFormat
                    name="valorFinalPlan"
                    value={newModel.valorFinalPlan}
                    thousandSeparator={","}
                    onChange={onValorFinalPlanChange}
                    prefix={"$"}
                    decimalSeparator={"."}
                  />
                </Col>
              </Row>
            )}
            {model.servicioDiaIndividual && (
              <Row className="mb-2">
                <LabelCol className="col-5">Días individuales:</LabelCol>
                <Col className="text-right">
                  {model.diasIndividualesGuarderia + " días x"}
                </Col>
                <Col className="text-right">
                  <CurrencyFormat
                    name="precioTotal"
                    value={newModel.valorFinalDiaIndividual}
                    thousandSeparator={","}
                    onChange={onValorFinalDiaIndividualChange}
                    prefix={"$"}
                    decimalSeparator={"."}
                  />
                </Col>
              </Row>
            )}
            {model.tarifaIda && (
              <Row className="mb-2">
                <LabelCol className="col-5">Transporte ida:</LabelCol>
                <Col className="text-right">{diasTransporte + " días x"}</Col>
                <Col className="text-right">
                  <CurrencyFormat
                    name="valorFinalIda"
                    value={newModel.valorFinalIda}
                    thousandSeparator={","}
                    onChange={onValorFinalIdaChange}
                    prefix={"$"}
                    decimalSeparator={"."}
                  />
                </Col>
              </Row>
            )}
            {model.tarifaVuelta && (
              <Row className="mb-2">
                <LabelCol className="col-5">Transporte vuelta:</LabelCol>
                <Col className="text-right">{diasTransporte + " días x"}</Col>
                <Col className="text-right">
                  <CurrencyFormat
                    name="valorFinalVuelta"
                    value={newModel.valorFinalVuelta}
                    thousandSeparator={","}
                    onChange={onValorFinalVueltaChange}
                    prefix={"$"}
                    decimalSeparator={"."}
                  />
                </Col>
              </Row>
            )}
            {model.tarifaIdaVuelta && (
              <Row className="mb-2">
                <LabelCol className="col-5">Transporte ida y vuelta:</LabelCol>
                <Col className="text-right">{diasTransporte + " días x"}</Col>
                <Col className="text-right">
                  <CurrencyFormat
                    name="valorFinalIdaVuelta"
                    value={newModel.valorFinalIdaVuelta}
                    thousandSeparator={","}
                    onChange={onValorFinalIdaVueltaChange}
                    prefix={"$"}
                    decimalSeparator={"."}
                  />
                </Col>
              </Row>
            )}
            <Row className="mb-2">
              <LabelCol className="col-5">Descuento: </LabelCol>
              <Col></Col>
              <Col className="text-right">
                <CurrencyFormat
                  name="descuento"
                  value={newModel.descuento}
                  thousandSeparator={","}
                  onChange={onDescuentoChange}
                  prefix={"$"}
                  decimalSeparator={"."}
                />
              </Col>
            </Row>
            <Row className="mb-2">
              <LabelCol className="col-5">Total: </LabelCol>
              <Col></Col>
              <Col className="text-right">
                <CurrencyFormat
                  value={newModel.total}
                  thousandSeparator={","}
                  disabled
                  prefix={"$"}
                  decimalSeparator={"."}
                />
              </Col>
            </Row>
          </section>

          <Row className="d-flex justify-content-between mt-2 mb-2">
            <Col>
              <Button variant="primary" onClick={volver}>
                <ArrowBackIcon sx={{ fontSize: 18 }} />
                Volver
              </Button>
            </Col>
            <Col className="d-flex justify-content-end gap-2">
              <Button
                variant="primary"
                onClick={onAgregarAlCarritoClick}
                size="sm"
                disabled={carritoPreparationOperationsIsLoading}
              >
                {!agregarAlCarritoIsLoading && (
                  <AddShoppingCart sx={{ fontSize: 18, marginRight: "2px" }} />
                )}
                {agregarAlCarritoIsLoading && (
                  <Spinner animation="border" size="sm" />
                )}
                Agregar
              </Button>
              <Button variant="primary" onClick={avanzar} size="sm">
                <PaymentIcon sx={{ fontSize: 18, marginRight: "2px" }} />
                Pagar
              </Button>
              <SuccessButton
                type={"button"}
                onClick={onConfirmarClick}
                variant="success"
                size="sm"
              >
                {!agendarIsLoading && (
                  <CheckIcon sx={{ fontSize: 18, marginRight: "2px" }} />
                )}
                {agendarIsLoading && <Spinner animation="border" size="sm" />}
                Finalizar{" "}
              </SuccessButton>
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  );
}

const recalcularTotal = (m: Model): Model => {
  const diasTransporte = m.fechasTransporte.length;

  const subTotal =
    (m.valorFinalPlan || 0) +
    (m.valorFinalDiaIndividual || 0) * (m.diasIndividualesGuarderia || 0) +
    (m.valorFinalIda || 0) * diasTransporte +
    (m.valorFinalVuelta || 0) * diasTransporte +
    (m.valorFinalIdaVuelta || 0) * diasTransporte;
  return { ...m, total: subTotal - m.descuento };
};

const SectionTitle = styled("h5")`
  font-weight: 500;
`;

const InfoRow = ({ label, value }: { label: string; value?: string }) => {
  return (
    <Row className="mb-2">
      <LabelCol className="col-4">{label + ":"}</LabelCol>
      <Col className="text-right">{value}</Col>
    </Row>
  );
};

const LabelCol = styled(Col)`
  font-weight: 500;
`;

const StyledCalendar = styled(Calendar)`
  width: 100%;
  border-radius: 5px;
  padding: 0%;
`;

const IconContainer = styled("span")`
  display: relative;
  margin-left: 8px;
  color: ${(props) => props.theme.colors.main};
`;

const exitoOptions: ExitoOption[] = [
  {
    description: "Realizar nuevo agendamiento",
    path: "/reservas/guarderia/crear",
    variant: "primary",
  },
  {
    description: "Ver listado",
    path: "/reservas/guarderia/listar",
    variant: "primary",
  },
  {
    description: "Ver carrito",
    path: "/carrito",
    variant: "secondary",
    isLast: true,
  },
];

export default Resumen;
